import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, unwrapObject } from "@/lib/api";
import { VISIT_CHANGED, customerLink, minutes, useVisitOutcomes, type Visit } from "@/lib/visits";
import { CheckOutDialog } from "./VisitDialogs";
import { useToast } from "@/context/ToastContext";
import { LOCAL_PREFIX, flushVisitQueue, pendingCount, pendingVisit } from "@/lib/visitQueue";

/** While the rep is checked in somewhere: where, for how long, and the way to check out, on every page */
export function VisitBar() {
  const { hasPermission } = useAuth();
  const allowed = hasPermission("visit.create");
  const { pathname } = useLocation();
  const [visit, setVisit] = useState<Visit | null>(null);
  const [checkingOut, setCheckingOut] = useState<Visit | null>(null);
  const [, tick] = useState(0);
  const { success, error } = useToast();
  // Loaded while there is signal, so checking out works later without it
  useVisitOutcomes();

  // The server's open visit; without signal, the one checked in on this phone
  const load = () =>
    api
      .get("/api/FieldVisit/Current")
      .then((r) => {
        const v = unwrapObject<Visit | null>(r, null);
        setVisit(v && v.id ? v : pendingVisit());
      })
      .catch(() => setVisit(pendingVisit()));

  // Visits kept on the phone go out as soon as there is a connection (and every minute while any wait)
  const send = async () => {
    if (!pendingCount()) return;
    const { sent, failed } = await flushVisitQueue();
    if (sent) success("Visits sent", `${sent} check-in${sent === 1 ? "" : "s"}/check-out${sent === 1 ? "" : "s"} made without signal ${sent === 1 ? "was" : "were"} sent.`);
    for (const f of failed) error("A visit couldn't be sent", f);
  };
  useEffect(() => {
    if (!allowed) return;
    void send();
    const t = window.setInterval(() => void send(), 60000);
    window.addEventListener("online", send);
    return () => {
      window.clearInterval(t);
      window.removeEventListener("online", send);
    };
  }, [allowed]);

  useEffect(() => {
    if (allowed) void load();
  }, [allowed, pathname]);
  useEffect(() => {
    const on = () => void load();
    window.addEventListener(VISIT_CHANGED, on);
    return () => window.removeEventListener(VISIT_CHANGED, on);
  }, []);
  // The minutes on site move on by themselves
  useEffect(() => {
    if (!visit) return;
    const t = window.setInterval(() => tick((n) => n + 1), 60000);
    return () => window.clearInterval(t);
  }, [visit?.id]);

  if (!allowed || !visit) return null;
  const onSite = visit.check_in_at ? Math.max(0, Math.round((Date.now() - new Date(visit.check_in_at).getTime()) / 60000)) : 0;
  return (
    <>
      <div role="status" className="fixed inset-x-3 bottom-3 z-40 mx-auto flex max-w-xl items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-2.5 shadow-lg">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-warning-soft text-warning">
          <MapPin className="size-4" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            On visit · {minutes(onSite)}
            {visit.id.startsWith(LOCAL_PREFIX) && <span className="text-warning"> · not sent yet (no signal)</span>}
          </span>
          <Link to={customerLink(visit)} className="truncate text-[13px] font-medium hover:underline">
            {visit.related_label}
          </Link>
        </span>
        <Button size="sm" onClick={() => setCheckingOut(visit)}>
          Check out
        </Button>
      </div>
      <CheckOutDialog visit={checkingOut} onOpenChange={(o) => !o && setCheckingOut(null)} onDone={() => setVisit(null)} />
    </>
  );
}
